import { Suspense, lazy, useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import axios from 'axios'

import { Layout } from './components/Layout'
import { useTenantStore } from './stores/tenantStore'
import { getApiErrorMessage } from './utils/errorMessages'

import { getApiBaseUrl } from './api/apiBase'

const Today = lazy(() => import('./pages/Today'))
const Habits = lazy(() => import('./pages/Habits'))
const HabitDetail = lazy(() => import('./pages/HabitDetail'))
const Journal = lazy(() => import('./pages/Journal'))
const Kanban = lazy(() => import('./pages/Kanban'))
const Finance = lazy(() => import('./pages/Finance'))
const Analytics = lazy(() => import('./pages/Analytics'))
const Chat = lazy(() => import('./pages/Chat'))
const Settings = lazy(() => import('./pages/Settings'))
const Upgrade = lazy(() => import('./pages/Upgrade'))
const PaymentHistory = lazy(() => import('./pages/PaymentHistory'))

const API_BASE = getApiBaseUrl()

function PageLoader() {
  return (
    <div className="p-4 space-y-4">
      <div className="skeleton h-8 w-48 mb-6" />
      <div className="skeleton-line" />
      <div className="skeleton-line w-3/4" />
      <div className="skeleton-line w-1/2" />
      <div className="grid grid-cols-2 gap-4 mt-6">
        <div className="skeleton h-24 rounded-xl" />
        <div className="skeleton h-24 rounded-xl" />
      </div>
    </div>
  )
}

function AuthScreen({ message }: { message: string }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-6 text-center">
      <div className="text-5xl mb-4">🤖</div>
      <h1 className="text-2xl font-bold text-primary mb-2">UzMentor</h1>
      <p className="text-text-secondary text-sm">{message}</p>
    </div>
  )
}

function App() {
  const { setToken, setTenant } = useTenantStore()
  const [authState, setAuthState] = useState<'loading' | 'ok' | 'error'>('loading')
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    const bootstrapAuth = async () => {
      const tg = window.Telegram?.WebApp
      tg?.ready()
      tg?.expand()

      const initData = tg?.initData
      const existingToken = localStorage.getItem('uzm_token')

      // 1) If we have a token, verify it first (mobile users often keep stale tokens).
      if (existingToken) {
        try {
          const meRes = await axios.get(`${API_BASE}/auth/me`, {
            headers: { Authorization: `Bearer ${existingToken}` },
          })
          setToken(existingToken)
          setTenant(meRes.data)
          setAuthState('ok')
          return
        } catch {
          // Token is stale/invalid — clear and fall back to Telegram initData auth.
          localStorage.removeItem('uzm_token')
          sessionStorage.removeItem('uzm-tenant-storage')
        }
      }

      // 2) Authenticate through Telegram Mini App initData.
      if (!initData) {
        if (import.meta.env.DEV) {
          setAuthState('ok')
        } else {
          setAuthState('error')
          setErrorMsg('Bu app faqat Telegram Mini App sifatida ochiladi.')
        }
        return
      }

      try {
        const res = await axios.post(`${API_BASE}/auth/telegram`, { init_data: initData })
        const { token: tokenData, tenant } = res.data
        setToken(tokenData.access_token)
        setTenant(tenant)
        setAuthState('ok')
      } catch (err) {
        console.error('Auth failed:', err)
        setAuthState('error')
        setErrorMsg(getApiErrorMessage(err, "Autentifikatsiya xatosi. Iltimos, qaytadan urinib ko'ring."))
      }
    }

    void bootstrapAuth()
  }, []) // Run once on mount only

  if (authState === 'loading') {
    return <AuthScreen message="Yuklanmoqda..." />
  }

  if (authState === 'error') {
    return <AuthScreen message={errorMsg} />
  }

  return (
    <BrowserRouter>
      <Layout>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Navigate to="/today" replace />} />
            <Route path="/today" element={<Today />} />
            <Route path="/habits" element={<Habits />} />
            <Route path="/habits/:habitId" element={<HabitDetail />} />
            <Route path="/journal" element={<Journal />} />
            <Route path="/kanban" element={<Kanban />} />
            <Route path="/finance" element={<Finance />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/chat" element={<Chat />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/upgrade" element={<Upgrade />} />
            <Route path="/payments" element={<PaymentHistory />} />
          </Routes>
        </Suspense>
      </Layout>
    </BrowserRouter>
  )
}

export default App
